import {
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import { Guid } from "client_legacy/ndaw/guid";
import {
	ZermeloInterests,
	ZermeloLiveInformationResponseModel,
	ZermeloLiveInformationRequestModel,
	getLiveInformation,
	ZermeloScheduleResponseModel
} from "client/Api/Workspace/LiveInformation/Zermelo/zermeloLiveInformation";
import { ensureNotNil } from "client/Misc/utils";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";
import { formatForDto, parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import { userProfileStore } from "client/Features/Settings/UserProfile/userProfileStore";

type ZermeloLiveInformationProviderOptions = {
	intervalInSeconds: number;
};

type ZermeloResourceId = {
	connectionId: number;
	date: Date;
};

interface InterestedZermeloParty extends InterestedParty<Guid, ZermeloResourceId> {
	updateDaySchedule?(response: ZermeloScheduleResponseModel, isTeacher: boolean): void;
	updateWeekSchedule?(response: ZermeloScheduleResponseModel, isTeacher: boolean): void;
}

class ZermeloLiveInformationProvider extends LiveInformationProvider<
	ZermeloLiveInformationProviderOptions,
	Guid,
	ZermeloResourceId,
	ZermeloInterests,
	InterestedZermeloParty,
	ZermeloLiveInformationResponseModel
> {

	constructor(options: ZermeloLiveInformationProviderOptions) {
		super(options);
	}

	protected async getDataFromBackend(
		entries: ReadonlyArray<[ZermeloResourceId, ZermeloInterests]>
	): Promise<ReadonlyArray<[ZermeloResourceId, ZermeloLiveInformationResponseModel]>> {
		const {
			userTimezone
		} = userProfileStore.getState();

		const requests: ZermeloLiveInformationRequestModel[] = entries.map(
			entry => this.convertResourceIdToRequest(
				entry[0],
				entry[1],
				userTimezone
			)
		);
		const responses: ReadonlyArray<ZermeloLiveInformationResponseModel> = await getLiveInformation(requests);

		return responses.map(response => [this.convertResponseToResourceId(response), response]);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<ZermeloInterests, ZermeloLiveInformationResponseModel>,
		response: [ZermeloInterests, ZermeloLiveInformationResponseModel]
	): void {
		const data: ZermeloLiveInformationResponseModel = response[1];

		if (data.interests.daySchedule) {
			cacheItem.retrievedInterests.daySchedule = true;
			cacheItem.data.data.daySchedule = data.data.daySchedule;
		}

		if (data.interests.weekSchedule) {
			cacheItem.retrievedInterests.weekSchedule = true;
			cacheItem.data.data.weekSchedule = data.data.weekSchedule;
		}

		cacheItem.data.isTeacher = data.isTeacher;
	}

	protected informParty(
		party: InterestedZermeloParty,
		cacheItem: [ZermeloInterests, ZermeloLiveInformationResponseModel]
	): void {
		const [retrievedInterests, response] = cacheItem;
		const interestedIn: ZermeloInterests = this.getInterestsForEnsuredParty(party);

		const {
			daySchedule,
			weekSchedule
		} = response.data;

		if (interestedIn.daySchedule && retrievedInterests.daySchedule && party.updateDaySchedule)
		{
			party.updateDaySchedule(
				ensureNotNil(
					daySchedule
				),
				response.isTeacher
			);
		}

		if (interestedIn.weekSchedule && retrievedInterests.weekSchedule && party.updateWeekSchedule)
		{
			party.updateWeekSchedule(
				ensureNotNil(
					weekSchedule
				),
				response.isTeacher
			);
		}
	}

	protected override createResourceMap<TValue>(): Map<ZermeloResourceId, TValue> {
		return new NormalizedMap<ZermeloResourceId, TValue>(
			(key) => `${key.connectionId}_${key.date}`
		);
	}

	private convertResourceIdToRequest(
		resourceId: ZermeloResourceId,
		interestedIn: ZermeloInterests,
		timezone: TimeZone
	): ZermeloLiveInformationRequestModel {

		return {
			connectionId: resourceId.connectionId,
			// We want the local timezone so we cannot use .toISOString which always outputs UTC
			date: formatForDto(resourceId.date, timezone),
			interestedIn: interestedIn
		};
	}

	private convertResponseToResourceId(
		response: ZermeloLiveInformationResponseModel
	): ZermeloResourceId {
		return {
			connectionId: response.connectionId,
			date: parseDtoDate(response.date)
		};
	}
}

export {
	type InterestedZermeloParty,
	type ZermeloLiveInformationProviderOptions,
	ZermeloLiveInformationProvider,
	type ZermeloResourceId
};